"use client";

import { useRouter } from "next/navigation";
import { useEffect, useRef, useState } from "react";
import Image from "next/image";
import { storePurchaseContext } from "@/libs/ga";
import { trackFunnel } from "@/libs/analytics";
import BeforeAfterSlider from "./BeforeAfterSlider";
import { freeUserBeforeAfterExamples } from "@/libs/beforeAfterExamples";

interface FreeUserConversionModalProps {
  isOpen: boolean;
  onClose: () => void;
  toolName?: string;
}

export default function FreeUserConversionModal({
  isOpen,
  onClose,
  toolName = "AI Tools"
}: FreeUserConversionModalProps) {
  const router = useRouter();
  const trackedRef = useRef(false);
  const [selectedExample, setSelectedExample] = useState(0);

  // Track when modal is shown (only once per opening)
  useEffect(() => {
    if (isOpen && !trackedRef.current) {
      trackFunnel('monetization', 'free_user_upload_blocked', {
        tool_name: toolName,
        source: 'upload_attempt',
        user_tier: 'free'
      });
      trackedRef.current = true;
    }

    if (!isOpen) {
      trackedRef.current = false;
    }
  }, [isOpen, toolName]);

  // Reset slider selection when modal opens
  useEffect(() => {
    if (isOpen) {
      setSelectedExample(0);
    }
  }, [isOpen]);

  const handleGetStarted = () => {
    // Store context for purchase tracking
    storePurchaseContext({
      price_id: '',
      mode: 'subscription',
      purchase_trigger: 'forced',
      source: 'free_user_upload_modal',
      tool_name: toolName,
      user_tier: 'free'
    });

    trackFunnel('monetization', 'pricing_viewed', {
      source: 'free_user_upload_modal',
      purchase_trigger: 'forced',
      tool_name: toolName,
      user_tier: 'free'
    });

    router.push('/dashboard/pricing');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[99999] bg-black/70 backdrop-blur-sm overflow-y-auto" onClick={onClose}>
      <div className="min-h-screen flex items-center justify-center p-0 md:p-4 md:py-8 -mt-2">
        <div
          className="bg-white rounded-none md:rounded-3xl shadow-2xl my-auto animate-scale-in relative"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-6 right-6 text-gray-400 hover:text-gray-600 transition-colors z-10"
            aria-label="Close"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          <div className="px-4 py-8 pb-16 md:p-2 md:pb-4">
            {/* Header */}
            <div className="text-center mb-8 mt-6">
              <div className="inline-flex items-center gap-2 bg-gradient-to-r from-primary to-blue-600 text-white px-4 py-2 rounded-full text-sm font-bold mb-4">
                ⚡ UNLOCK PREMIUM AI TOOLS
              </div>
              <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 mb-4">
                Transform Your Photos Like a Pro
              </h2>
              <p className="text-lg md:text-xl text-gray-600 max-w-2xl mx-auto px-4">
                Join <span className="font-bold text-primary">18M+ users</span> who've created <span className="font-bold text-primary">55M+ photos</span> with our professional AI tools
              </p>
            </div>

            {/* Before/After Mega Slider */}
            <div className="mb-8">
              {/*
              <h3 className="text-2xl font-bold text-gray-900 text-center mb-6">
                See What You Can Create
              </h3>
              */}

              {/* Desktop: Vertical thumbnails on left, Mobile: Horizontal on bottom */}
              <div className="flex flex-col md:flex-row md:items-center md:justify-center gap-4 md:gap-6 mx-auto">
                {/* Thumbnail Selector - Vertical on desktop, horizontal on mobile */}
                <div className="flex md:flex-col justify-center gap-3 order-2 md:order-1 flex-shrink-0">
                  {freeUserBeforeAfterExamples.map((example, idx) => (
                    <button
                      key={idx}
                      onClick={() => setSelectedExample(idx)}
                      className={`relative rounded-lg md:rounded-xl overflow-hidden transition-all duration-200 flex-shrink-0 ${
                        selectedExample === idx
                          ? 'ring-3 md:ring-4 ring-primary scale-105'
                          : 'ring-2 ring-gray-300 hover:ring-primary/50 hover:scale-105'
                      }`}
                    >
                      <div className="w-16 h-16 md:w-20 md:h-20 relative">
                        <Image
                          src={example.after}
                          alt={example.title}
                          fill
                          className="object-cover"
                          sizes="(max-width: 768px) 64px, 80px"
                        />
                      </div>
                      {/* Tool name overlay - hidden on mobile */}
                      <div className="hidden md:block absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent text-white text-[10px] font-bold py-1.5 px-1 text-center">
                        {example.title}
                      </div>
                    </button>
                  ))}
                </div>

                {/* Large Interactive Slider */}
                <div className="flex-1 max-w-lg order-1 md:order-2">
                  <BeforeAfterSlider
                    key={selectedExample}
                    beforeImage={freeUserBeforeAfterExamples[selectedExample].before}
                    afterImage={freeUserBeforeAfterExamples[selectedExample].after}
                    title={freeUserBeforeAfterExamples[selectedExample].title}
                    description={freeUserBeforeAfterExamples[selectedExample].description}
                  />
                </div>
              </div>
            </div>

            {/* Features List */}
            {/*
            <div className="mb-8">
              <div className="grid md:grid-cols-2 gap-4 max-w-3xl mx-auto">
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">27 Professional Tools</p>
                    <p className="text-sm text-gray-600">Headshots, avatars, backgrounds & more</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">1 Photo → Results in 15s</p>
                    <p className="text-sm text-gray-600">Process and download fast</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">High-Quality Results</p>
                    <p className="text-sm text-gray-600">Professional AI processing</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">Flexible Plans</p>
                    <p className="text-sm text-gray-600">Subscription or pay-as-you-go credits</p>
                  </div>
                </div>
              </div>
            </div>
            */}

              {/* Competitive Advantages */}
              <div className="mb-6 max-w-3xl mx-auto">
                <div className="grid grid-cols-3 gap-4 md:flex md:items-center md:justify-center md:gap-12 pl-0 pr-0 md:px-4">
                  <div className="flex flex-col md:flex-row items-center md:items-center gap-2">
                    <div className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <div className="text-center md:text-left">
                      <p className="font-bold text-gray-900 text-base">From 1 Photo</p>
                      <p className="text-sm text-gray-700">That's all you need</p>
                    </div>
                  </div>
                  <div className="flex flex-col md:flex-row items-center md:items-center gap-2">
                    <div className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <div className="text-center md:text-left">
                      <p className="font-bold text-gray-900 text-base">Results in 15s</p>
                      <p className="text-sm text-gray-700">Ready to download</p>
                    </div>
                  </div>
                  <div className="flex flex-col md:flex-row items-center md:items-center gap-2">
                    <div className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <div className="text-center md:text-left">
                      <p className="font-bold text-gray-900 text-base">Studio Quality</p>
                      <p className="text-sm text-gray-700">Realistic results</p>
                    </div>
                  </div>
                </div>
              </div>

            {/* Pricing CTA Box */}
            <div className="bg-gradient-to-r from-orange-500 to-pink-600 rounded-2xl p-8 text-white text-center max-w-[620px] mx-auto">
              <p className="text-xl font-bold mb-3">Starting from $9</p>
              <p className="text-white/90 text-sm mb-6">Subscription or pay-as-you-go • 27 tools • 1,172+ styles</p>

              <button
                onClick={handleGetStarted}
                className="max-w-xl w-full bg-white text-orange-600 px-8 py-4 rounded-xl font-bold text-lg hover:bg-gray-100 transform hover:scale-105 transition-all duration-200 shadow-lg hover:shadow-xl"
              >
                Get Started Now →
              </button>
            </div>

            {/* Fine Print */}
            <p className="text-center text-xs text-gray-500 pt-4">
                Credits rollover • Cancel anytime
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

